import { sentryService } from './sentry';
import { store } from '@web-client/state/store';
import { addNotification } from '@web-client/state/slices/notifications';

class ErrorHandlerService {
  private static instance: ErrorHandlerService;
  private isInitialized = false;
  private isComponentHandlingError = false;

  private constructor() {}

  public static getInstance(): ErrorHandlerService {
    if (!ErrorHandlerService.instance) {
      ErrorHandlerService.instance = new ErrorHandlerService();
    }
    return ErrorHandlerService.instance;
  }

  public setComponentErrorHandlingActive(active: boolean): void {
    this.isComponentHandlingError = active;
  }

  public setupGlobalErrorHandling(): void {
    if (this.isInitialized) {
      return;
    }

    // Check Sentry configuration and warn if not properly set up
    const sentryStatus = sentryService.getConfigurationStatus();
    if (!sentryStatus.isProperlyConfigured) {
      console.warn(
        '⚠️ ERROR HANDLER WARNING: Sentry is not properly configured. ' +
        'Errors will be logged locally but not sent to error tracking service. ' +
        'Check Sentry configuration warnings above.'
      );
    }

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      event.preventDefault();

      const error = event.reason;
      const errorMessage = error?.message || 'Unhandled Promise Rejection';

      // Capture the error in Sentry
      sentryService.captureError(error || new Error(errorMessage), {
        type: 'unhandledrejection',
        promise: event.promise,
        location: window.location.href,
      });

      // Only show global notification if component isn't handling errors
      if (!this.isComponentHandlingError) {
        store.dispatch(addNotification({
          message: 'An unexpected error occurred. Please try refreshing the page.',
          type: 'error',
        }));
      }
    });

    // Handle unhandled errors
    window.addEventListener('error', (event) => {
      event.preventDefault();

      const error = new Error(event.message);
      error.stack = event.error?.stack || event.message;

      // Capture the error in Sentry
      sentryService.captureError(error, {
        type: 'unhandlederror',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        location: window.location.href,
      });

      // Only show global notification if component isn't handling errors
      if (!this.isComponentHandlingError) {
        store.dispatch(addNotification({
          message: 'An unexpected error occurred. Please try refreshing the page.',
          type: 'error',
        }));
      }
    });

    // REMOVED: All global function overrides to prevent infinite loops and maintain system stability
    // - console.error override (was causing infinite loops with Sentry)
    // - console.warn override (was causing infinite loops with Sentry)
    // - fetch override (was interfering with normal HTTP requests)
    // - XMLHttpRequest override (was interfering with normal HTTP requests)

    this.isInitialized = true;
    console.log('Global error handling initialized (non-invasive mode)');
  }

  public cleanupGlobalErrorHandling(): void {
    // Note: We can't easily remove event listeners without keeping references
    // This is mainly for completeness
    console.log('Global error handling cleanup requested');
  }

  // Utility function to wrap async functions with error handling
  public withErrorHandling<T extends unknown[], R>(
    fn: (...args: T) => R | Promise<R>,
    context?: string
  ) {
    return (...args: T): R | Promise<R> => {
      try {
        const result = fn(...args);

        // If it's a promise, handle it
        if (result instanceof Promise) {
          return result.catch((error) => {
            sentryService.captureError(error instanceof Error ? error : new Error(String(error)), {
              type: 'async_function_error',
              functionName: fn.name,
              context,
              args: args.map(arg => String(arg)),
              location: window.location.href,
            });
            throw error;
          });
        }

        return result;
      } catch (error) {
        sentryService.captureError(error instanceof Error ? error : new Error(String(error)), {
          type: 'sync_function_error',
          functionName: fn.name,
          context,
          args: args.map(arg => String(arg)),
          location: window.location.href,
        });
        throw error;
      }
    };
  }

  // Utility function to wrap synchronous functions with error handling
  public withSyncErrorHandling<T extends unknown[], R>(
    fn: (...args: T) => R,
    context?: string
  ) {
    return (...args: T): R => {
      try {
        return fn(...args);
      } catch (error) {
        sentryService.captureError(error instanceof Error ? error : new Error(String(error)), {
          type: 'sync_function_error',
          functionName: fn.name,
          context,
          args: args.map(arg => String(arg)),
          location: window.location.href,
        });
        throw error;
      }
    };
  }

  // Utility function to create errors with context
  public createError(message: string, context?: Record<string, unknown>): Error {
    const error = new Error(message);
    sentryService.captureError(error, context);
    return error;
  }
}

// Export singleton instance
export const errorHandlerService = ErrorHandlerService.getInstance();

// Export individual methods for backward compatibility
export const setupGlobalErrorHandling = () => errorHandlerService.setupGlobalErrorHandling();
export const cleanupGlobalErrorHandling = () => errorHandlerService.cleanupGlobalErrorHandling();
export const withErrorHandling = <T extends unknown[], R>(
  fn: (...args: T) => R | Promise<R>,
  context?: string
) => errorHandlerService.withErrorHandling(fn, context);
export const withSyncErrorHandling = <T extends unknown[], R>(
  fn: (...args: T) => R,
  context?: string
) => errorHandlerService.withSyncErrorHandling(fn, context);
export const createError = (message: string, context?: Record<string, unknown>) =>
  errorHandlerService.createError(message, context);
